/**
 * Portal App Logger - Ships to Axiom in production, console in dev
 *
 * Uses @plasma/logger for core infrastructure. This file provides
 * portal-specific configuration and event constants.
 *
 * @example
 * ```ts
 * import { logger } from '~/lib/logger';
 *
 * // Simple logging
 * logger.info('menu_updated', { menuId: '123' });
 * logger.error('payment_failed', { error: 'Card declined' });
 *
 * // With context
 * logger
 *   .withUser({ user_id: userId })
 *   .withOrg({ organization_id: orgId })
 *   .info('settings_changed', { setting: 'tax_rate' });
 * ```
 */

import { initLogger } from "@plasma/logger/index";

import { env } from "~/env";

initLogger({
  service: "portal-app",
  environment:
    env.PUBLIC_ENVIRONMENT ??
    (import.meta.env.DEV ? "development" : "production"),
  isDev: import.meta.env.DEV,
  platform: "web",
  axiomToken: env.PUBLIC_AXIOM_TOKEN,
  axiomDataset: env.PUBLIC_AXIOM_DATASET,
  axiomOrgId: env.PUBLIC_AXIOM_ORG_ID,
});

// ============================================================================
// STANDARDIZED EVENT NAMES
// ============================================================================

export const PortalEvents = {
  // Auth events
  USER_SIGNED_IN: "user_signed_in",
  USER_SIGNED_OUT: "user_signed_out",
  ORG_SWITCHED: "org_switched",
  AUTH_LAYOUT_MOUNTED: "auth_layout_mounted",

  // Navigation & UI
  TAB_NAVIGATION: "tab_navigation",
  CONNECTION_STATUS_CHANGED: "connection_status_changed",
  ERROR_BOUNDARY_TRIGGERED: "error_boundary_triggered",

  // Menu management
  MENU_CREATED: "menu_created",
  MENU_UPDATED: "menu_updated",
  MENU_DELETED: "menu_deleted",
  MENU_ITEM_CREATED: "menu_item_created",
  MENU_ITEM_UPDATED: "menu_item_updated",
  MENU_ITEM_DELETED: "menu_item_deleted",
  CATEGORY_CREATED: "category_created",
  CATEGORY_UPDATED: "category_updated",
  CATEGORY_DELETED: "category_deleted",
  MODIFIER_GROUP_CREATED: "modifier_group_created",
  MODIFIER_GROUP_UPDATED: "modifier_group_updated",
  MODIFIER_GROUP_DELETED: "modifier_group_deleted",
  MODIFIER_GROUP_DUPLICATED: "modifier_group_duplicated",

  // Location management
  LOCATION_CREATED: "location_created",
  LOCATION_UPDATED: "location_updated",
  LOCATION_PROFILE_UPDATED: "location_profile_updated",

  // Device & Terminal management
  DEVICE_CREATED: "device_created",
  DEVICE_UPDATED: "device_updated",
  DEVICE_DELETED: "device_deleted",
  TERMINAL_ADDED: "terminal_added",
  TERMINAL_REMOVED: "terminal_removed",
  TERMINAL_SETUP_STARTED: "terminal_setup_started",
  TERMINAL_SETUP_COMPLETED: "terminal_setup_completed",
  TERMINAL_SETUP_FAILED: "terminal_setup_failed",
  READER_REGISTERED: "reader_registered",
  READER_DELETED: "reader_deleted",

  // Printer management
  PRINTER_TAG_CREATED: "printer_tag_created",
  PRINTER_TAG_UPDATED: "printer_tag_updated",
  PRINTER_TAG_DELETED: "printer_tag_deleted",

  // Employee management
  EMPLOYEE_CREATED: "employee_created",
  EMPLOYEE_UPDATED: "employee_updated",
  EMPLOYEE_DELETED: "employee_deleted",

  // Organization management
  MEMBER_INVITED: "member_invited",
  MEMBER_ROLE_CHANGED: "member_role_changed",
  MEMBER_REMOVED: "member_removed",

  // Settings
  TAX_RATE_SYNCED: "tax_rate_synced",
  TAX_CATEGORY_CREATED: "tax_category_created",
  TAX_CATEGORY_UPDATED: "tax_category_updated",
  TAX_CATEGORY_DELETED: "tax_category_deleted",
  SALES_CATEGORY_CREATED: "sales_category_created",
  SALES_CATEGORY_UPDATED: "sales_category_updated",
  SALES_CATEGORY_DELETED: "sales_category_deleted",
  DISCOUNT_SETTINGS_UPDATED: "discount_settings_updated",

  // Billing
  PAYMENT_METHOD_UPDATED: "payment_method_updated",
  SUBSCRIPTION_UPDATED: "subscription_updated",
  BILLING_PORTAL_OPENED: "billing_portal_opened",

  // AI features
  AI_CHAT_STARTED: "ai_chat_started",
  AI_CHAT_OPENED: "ai_chat_opened",
  AI_CHAT_CLOSED: "ai_chat_closed",
  AI_MESSAGE_SENT: "ai_message_sent",
  AI_MESSAGE_FAILED: "ai_message_failed",
  AI_WRITE_CONFIRMED: "ai_write_confirmed",
  AI_WRITE_REJECTED: "ai_write_rejected",
  AI_THREAD_CREATED: "ai_thread_created",
  AI_THREAD_ARCHIVED: "ai_thread_archived",
  AI_THREAD_SELECTED: "ai_thread_selected",
  AI_THREAD_STARTED_NEW: "ai_thread_started_new",
  AI_THREAD_RENAMED: "ai_thread_renamed",
  AI_THREAD_DELETE_CONFIRMED: "ai_thread_delete_confirmed",
  AI_THREAD_SEARCH_STARTED: "ai_thread_search_started",
  AI_SIDEBAR_OPENED: "ai_sidebar_opened",
  AI_SIDEBAR_CLOSED: "ai_sidebar_closed",
  AI_FLOATING_BUTTON_CLICKED: "ai_floating_button_clicked",
  AI_MODE_CHANGED: "ai_mode_changed",
  AI_MODE_TOGGLED: "ai_mode_toggled",
  AI_FOCUS_CHANGED: "ai_focus_changed",
  AI_PREFERENCES_LOADED: "ai_preferences_loaded",
  AI_PREFERENCES_CHANGED: "ai_preferences_changed",
  AI_SEARCH_STARTED: "ai_search_started",
  AI_SEARCH_COMPLETED: "ai_search_completed",
  AI_SEARCH_FAILED: "ai_search_failed",
  AI_SEARCH_CLEARED: "ai_search_cleared",
  AI_FILE_UPLOAD_STARTED: "ai_file_upload_started",
  AI_FILE_UPLOAD_COMPLETED: "ai_file_upload_completed",
  AI_FILE_UPLOAD_FAILED: "ai_file_upload_failed",
  AI_FILE_VALIDATION_FAILED: "ai_file_validation_failed",
  AI_FILE_MESSAGE_SENT: "ai_file_message_sent",
  AI_FILE_DELETED: "ai_file_deleted",

  // File operations
  FILE_UPLOADED: "file_uploaded",
  FILE_UPLOAD_FAILED: "file_upload_failed",

  // Onboarding
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Payment setup
  PAYMENT_SETUP_STARTED: "payment_setup_started",
  PAYMENT_SETUP_COMPLETED: "payment_setup_completed",
  PAYMENT_SETUP_FAILED: "payment_setup_failed",

  // Stripe Connect
  STRIPE_CONNECT_INITIALIZED: "stripe_connect_initialized",
  STRIPE_CONNECT_ERROR: "stripe_connect_error",
  STRIPE_CONNECT_THEME_UPDATED: "stripe_connect_theme_updated",

  // Reports
  REPORT_GENERATED: "report_generated",
  REPORT_EXPORTED: "report_exported",

  // Order events
  ORDER_CANCEL_CLICKED: "order_cancel_clicked",

  // Rate limiting
  RATE_LIMIT_STATUS_CHANGED: "rate_limit_status_changed",

  // Error events
  MUTATION_FAILED: "mutation_failed",
  API_ERROR: "api_error",
  UNHANDLED_ERROR: "unhandled_error",
} as const;

export type PortalEventName = (typeof PortalEvents)[keyof typeof PortalEvents];

export type { LogContext } from "@plasma/logger/index";
export {
  createLogger,
  Logger,
  logger,
  sanitizeData,
} from "@plasma/logger/index";
